import {createSlice} from "@reduxjs/toolkit";
import {FieldNames} from "models/fields";
import {setIncidentAssigneesSuccess} from "./assigneesSlice";
import {
  setIncidentStatus,
  setIncidentStatusFailure,
  setIncidentStatusSuccess
} from "./incidentsSlice";

const initialState = {
  auditLog: [],
  auditLogTotalCount: 0,
  id: "",
  incident: {},
  loadingBundle: false,
  loadingIncident: false,
  loadingIncidentAuditLog: false,
  loadingNotes: false,
  notes: [],
  notesTotalCount: 0,
  patchingField: null,
  playbook: {},
  refreshing: false,
  relatedIncidents: [],
  savingNote: false
};

export const incidentDetailSlice = createSlice({
  name: "incidentDetail",
  initialState,
  reducers: {
    getIncident: (state) => {
      state.loadingIncident = true;
      state.loadingBundle = true;
      state.loadingPlaybook = true;
    },
    getIncidentSuccess: (state, action) => {
      state.id = action.payload.id;
      state.incident = action.payload.incident;
      state.loadingIncident = false;
      state.loadingBundle = false;
      state.refreshing = false;
    },
    getIncidentFailure: () => ({...initialState}),
    patchIncident: (state, action) => {
      const {patch} = action.payload;
      state.patchingField = Object.keys(patch)[0];
      state.refreshing = true;
    },
    patchIncidentSuccess: (state, action) => {
      const {patch} = action.payload;
      const patchKey = Object.keys(patch)[0];
      state.incident = {
        ...state.incident,
        [patchKey]: patch[patchKey]
      };
      state.refreshing = false;
    },
    patchIncidentFailure: (state) => {
      state.patchingField = null;
      state.refreshing = false;
    },
    getIncidentAuditLog: (state) => {
      state.loadingIncidentAuditLog = true;
    },
    getIncidentAuditLogSuccess: (state, action) => {
      const {data, totalLogCount} = action.payload;
      state.loadingIncidentAuditLog = false;
      state.auditLog = data ?? [];
      state.auditLogTotalCount = totalLogCount;
    },
    getIncidentAuditLogFailure: (state) => {
      state.loadingIncidentAuditLog = false;
    },
    getIncidentNotes: (state) => {
      state.loadingNotes = true;
    },
    getIncidentNotesSuccess: (state, action) => {
      const {dataRows, totalRows} = action.payload;
      state.loadingNotes = false;
      state.notes = dataRows ?? [];
      state.notesTotalCount = totalRows ?? 0;
    },
    getIncidentNotesFailure: (state) => {
      state.loadingNotes = false;
    },
    postIncidentNote: (state) => {
      state.savingNote = true;
    },
    postIncidentNoteSuccess: (state, action) => {
      const {limit, sortBy, ...newNote} = action.payload;
      const sortByNewest = sortBy === `desc`;
      const atPageLimit = state.notes.length >= limit;
      state.savingNote = false;
      state.notesTotalCount += 1;

      if (atPageLimit && sortByNewest) {
        state.notes.pop(); // Make room for the new note
        state.notes.unshift(newNote);
      } else if (!atPageLimit) {
        sortByNewest ? state.notes.unshift(newNote) : state.notes.push(newNote);
      }
    },
    postIncidentNoteFailure: (state) => {
      state.savingNote = false;
    },
    clearIncidentDetail: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(setIncidentStatus, (state) => {
        state.refreshing = true;
      })
      .addCase(setIncidentStatusSuccess, (state, action) => {
        const {incidents} = action.payload;
        const matched = incidents.find((incident) => incident.id === state.id);
        if (matched) {
          state.incident = {
            ...state.incident,
            status: matched.status
          };
        }
        state.refreshing = false;
      })
      .addCase(setIncidentStatusFailure, (state) => {
        state.refreshing = false;
      })
      .addCase(setIncidentAssigneesSuccess, (state, action) => {
        const {assignees} = action.payload;
        state.incident = {...state.incident, assignees};
      })
      .addDefaultCase(() => {});
  }
});

export const {
  clearIncidentDetail,
  getIncident,
  getIncidentAuditLog,
  getIncidentAuditLogFailure,
  getIncidentAuditLogSuccess,
  getIncidentFailure,
  getIncidentNotes,
  getIncidentNotesFailure,
  getIncidentNotesSuccess,
  getIncidentSuccess,
  patchIncident,
  patchIncidentFailure,
  patchIncidentSuccess,
  postIncidentNote,
  postIncidentNoteFailure,
  postIncidentNoteSuccess,
  updateIncidentUi
} = incidentDetailSlice.actions;

export default incidentDetailSlice.reducer;
